import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  makeStyles,
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  section: {
    margin: theme.spacing(1, 0, 1.5),
    ...theme.typography.sectionLabel,
  },
}));

const empty = {
  name: "",
  planId: "",
  dueDate: "",
  email: "",
  phone: "",
  document: "",
  active: true,
  adminName: "",
  adminEmail: "",
  adminPassword: "",
};

const CompanyModal = ({ open, onClose, company, plans, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      company
        ? {
            ...empty,
            name: company.name,
            planId: company.planId || "",
            dueDate: company.dueDate || "",
            email: company.email || "",
            phone: company.phone || "",
            document: company.document || "",
            active: company.active,
          }
        : { ...empty, planId: plans[0]?.id || "" }
    );
  }, [open, company, plans]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      planId: form.planId || null,
      dueDate: form.dueDate || null,
      email: form.email,
      phone: form.phone,
      document: form.document,
      active: form.active,
    };
    try {
      if (company) {
        await api.put(`/companies/${company.id}`, payload);
      } else {
        await api.post("/companies", {
          ...payload,
          admin: {
            name: form.adminName,
            email: form.adminEmail,
            password: form.adminPassword,
          },
        });
      }
      toast.success(i18n.t("companies.toasts.saved"));
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const text = (field, props = {}) => (
    <TextField
      label={i18n.t(`companyModal.form.${field}`)}
      value={form[field]}
      onChange={(e) => set(field)(e.target.value)}
      variant="outlined"
      size="small"
      fullWidth
      {...props}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t(company ? "companyModal.title.edit" : "companyModal.title.add")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <div className={classes.grid}>
            {text("name", { required: true, autoFocus: true })}
            <TextField
              select
              label={i18n.t("companyModal.form.plan")}
              value={form.planId}
              onChange={(e) => set("planId")(e.target.value)}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">{i18n.t("companies.noPlan")}</MenuItem>
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name}
                </MenuItem>
              ))}
            </TextField>
            {text("dueDate", { type: "date", InputLabelProps: { shrink: true } })}
            {text("document")}
            {text("email", { type: "email" })}
            {text("phone")}
          </div>
          {company && (
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!form.active}
                  onChange={(e) => set("active")(e.target.checked)}
                />
              }
              label={i18n.t("companyModal.form.active")}
            />
          )}
          {!company && (
            <>
              <div className={classes.section}>{i18n.t("companyModal.form.admin")}</div>
              <div className={classes.grid}>
                {text("adminName", { required: true })}
                {text("adminEmail", { required: true, type: "email" })}
                {text("adminPassword", {
                  required: true,
                  type: "password",
                  inputProps: { minLength: 5 },
                  autoComplete: "new-password",
                })}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("companyModal.buttons.cancel")}
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving}>
            {i18n.t("companyModal.buttons.ok")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CompanyModal;
